import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLoginMutation } from "../../app/api/authApi";
import { setCredentials } from "../../app/features/authSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

const inputClass =
  "w-full bg-transparent border-b border-gray-200 py-3 text-[14px] text-itl-navy placeholder-gray-300 focus:outline-none focus:border-itl-navy transition-colors duration-200";

function errorMessage(error: unknown): string {
  const data = (error as { data?: { error?: string } })?.data;
  if (data?.error) return data.error;
  const status = (error as { status?: number | string })?.status;
  if (status === "FETCH_ERROR") {
    return "Could not reach the server. Check your connection and try again.";
  }
  return "Something went wrong. Please try again.";
}

export default function AdminLogin() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAppSelector((state) => state.auth);

  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [login, { isLoading, isError, error }] = useLoginMutation();

  const from =
    (location.state as { from?: string } | null)?.from || "/admin";

  // Already signed in — skip the form
  if (token && user?.role === "admin") {
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const result = await login({
        email: form.email.trim().toLowerCase(),
        password: form.password,
      }).unwrap();

      dispatch(setCredentials({ token: result.token, user: result.user }));
      navigate(from, { replace: true });
    } catch {
      // isError renders the message below
    }
  }

  return (
    <div className="min-h-screen bg-itl-cream font-sans text-itl-navy flex flex-col">
      <div className="px-6 lg:px-12 py-8">
        <Link to="/" className="inline-flex items-center gap-3">
          <img
            src="/images/logo-blue.png"
            alt="Incubator Tech Limited"
            className="h-8 w-auto object-contain"
          />
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="w-full max-w-[420px] bg-white border border-gray-100 px-8 sm:px-10 py-12"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-itl-blue">
            Admin Console
          </p>
          <h1 className="mt-3 font-serif text-[30px] leading-tight">
            Sign in
          </h1>
          <p className="mt-3 text-[14px] leading-relaxed text-gray-500">
            Access project briefs and enquiries submitted through the site.
          </p>

          <form onSubmit={handleSubmit} className="mt-10 flex flex-col gap-7">
            <div className="flex flex-col gap-2">
              <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                Email
              </label>
              <input
                type="email"
                autoComplete="username"
                required
                value={form.email}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, email: e.target.value }))
                }
                placeholder="you@incubatortech.org"
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={form.password}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, password: e.target.value }))
                  }
                  placeholder="••••••••"
                  className={`${inputClass} pr-16`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400 hover:text-itl-navy transition-colors"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            {isError && (
              <p className="text-[13px] text-red-600">{errorMessage(error)}</p>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="mt-2 bg-itl-navy text-white text-[12px] font-semibold uppercase tracking-[0.18em] py-4 hover:bg-itl-blue transition-colors duration-200 disabled:opacity-50 disabled:hover:bg-itl-navy"
            >
              {isLoading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mt-8 text-[12px] text-gray-400">
            <Link to="/" className="hover:text-itl-navy transition-colors">
              ← Back to incubatortech.org
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
